import React, { useEffect, useRef, useState } from 'react';
import { isPrerender } from '../Utility/prerender';

// Counts up to a value such as "1,200+" or "100%" when it scrolls into view.
// The full value is rendered on the server and without JavaScript.
const CountUp: React.FC<{ value: string }> = ({ value }) => {
  const ref = useRef<HTMLSpanElement>(null);
  const match = value.match(/^([\d,]+)(.*)$/);
  const target = match ? parseInt(match[1].replace(/,/g, ''), 10) : 0;
  const suffix = match ? match[2] : '';
  const useCommas = match ? match[1].includes(',') : false;
  const [display, setDisplay] = useState(value);

  useEffect(() => {
    const el = ref.current;
    if (!el || !match || typeof IntersectionObserver === 'undefined' || isPrerender()) return;
    if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const format = (n: number) => (useCommas ? n.toLocaleString('en-US') : String(n)) + suffix;
    setDisplay(format(0));
    let frame = 0;
    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries[0].isIntersecting) return;
        observer.disconnect();
        const start = performance.now();
        const duration = 1600;
        const tick = (now: number) => {
          const p = Math.min((now - start) / duration, 1);
          const eased = 1 - Math.pow(1 - p, 3);
          setDisplay(format(Math.round(target * eased)));
          if (p < 1) frame = requestAnimationFrame(tick);
        };
        frame = requestAnimationFrame(tick);
      },
      { threshold: 0.6 }
    );
    observer.observe(el);
    return () => {
      observer.disconnect();
      cancelAnimationFrame(frame);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <span ref={ref} aria-label={value}>
      {display}
    </span>
  );
};

export default CountUp;
